import { Divider, HStack, Stack, Switch } from '@chakra-ui/react';
import { FooterBtnGroups, RepositoryDetails } from '../components';
import {
	CREATE_EDITING_HOST_FOR_BYOC,
	CREATE_EDITING_HOST_FOR_STARTER_KIT,
	EH_ADO_ORGANIZATION,
	EH_ADO_PROJECT_NAME,
	EH_CONNECTION_NAME,
	EH_ENVIRONMENT_NAME,
	EH_ENVIRONMENT_VARIABLES,
	EH_PROVIDER,
	EH_REPO_BRANCH,
	EH_REPOSITORY,
	EH_REPOSITORY_ID,
	EH_SOURCE_CONTROL_INTEGRATION_ID,
	EnvironmentTypesEnum,
	isEditingHostEnabled,
} from '../helpers';
import { ESourceControl } from '../../../../@xmcloud/types';
import { thirdStep } from '../../../../@xmcloud/core/messages/en';
import { useValidateSourceControlIntegrationV2 } from '../../../services/repositories';
import { useGetSourceControlAccessToken } from '../../../services/accessToken';
import { HeadingLg, Text } from '../../../shared-components';
import { EnvVars } from '../../../features/custom-environment-variables';
import {
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	ChangeEvent,
} from 'react';
import {
	AdoRepository,
	EnvironmentName,
	EnvironmentVariables,
	GithubRepository,
	Options,
	SetupProviderAccount,
} from '../../../features/common-env-fields';
import { useCreateProject, useValidateCmAndEhSteps } from '../hooks';
import { FormikValues } from '../hooks/CreateProjectContext';

const { info4, nameEhEnv, ehPageTitle, ehPageTitleOptional, ehSubTitle } =
	thirdStep;

const { EH } = EnvironmentTypesEnum;
const { ADO, GitHub } = ESourceControl;

const repoLookup = {
	[GitHub]: (props: any) => <GithubRepository {...props} />,
	[ADO]: (props: any) => <AdoRepository {...props} />,
	'': () => <></>,
};

export const SetupEditingHost: React.FC = () => {
	const {
		values,
		errors,
		handleChange,
		setFieldTouched,
		touched,
		prev,
		setValues,
		setErrors,
		setFieldValue,
		setTouched,
		isByoc,
	} = useCreateProject();

	const initializedRef = useRef(false);
	const editingHostSwitchLabelId = useId();

	const sourceControlIntegrationId = values[EH_SOURCE_CONTROL_INTEGRATION_ID];

	const {
		isFetching: isSourceControlIntegrationLoading,
		isIntegrationIdValidationValid,
		integrationValidationErrorMsg,
	} = useValidateSourceControlIntegrationV2({
		integrationId: sourceControlIntegrationId,
		enabled: !!sourceControlIntegrationId,
	});

	// Memoize this computed value
	const shouldCallAccessTokenApi = useMemo(
		() =>
			isIntegrationIdValidationValid &&
			!isSourceControlIntegrationLoading,
		[isIntegrationIdValidationValid, isSourceControlIntegrationLoading],
	);

	const { data: accessTokenData, isLoading: isLoadingAccessToken } =
		useGetSourceControlAccessToken(
			sourceControlIntegrationId,
			shouldCallAccessTokenApi,
		);

	// Memoize these computed values
	const accessToken = useMemo(
		() => accessTokenData?.data.accessToken || '',
		[accessTokenData],
	);

	const isLoading = useMemo(
		() => isSourceControlIntegrationLoading || isLoadingAccessToken,
		[isSourceControlIntegrationLoading, isLoadingAccessToken],
	);

	const { handleNext, isLoading: validationIsLoading } =
		useValidateCmAndEhSteps({
			isIntegrationIdValidationValid,
			integrationValidationErrorMsg,
			nameSpace: EH,
		});

	const CurrentRepository = useMemo(
		() => repoLookup[values[EH_PROVIDER]],
		[values],
	);
	useEffect(() => {
		if (!initializedRef.current) {
			setFieldValue(
				isByoc
					? CREATE_EDITING_HOST_FOR_BYOC
					: CREATE_EDITING_HOST_FOR_STARTER_KIT,
				true,
			);
			initializedRef.current = true;
		}
	}, [isByoc, setFieldValue]);

	useEffect(() => {
		if (isByoc && !values.ehSourceControlIntegrationId) {
			const {
				cmProvider,
				cmSourceControlIntegrationId,
				cmSourceControlIntegrationName,
				cmRepositoryBranch,
				cmRepository,
				cmRepositoryId,
				cmAdoOrganization,
				cmAdoProjectName,
			} = values;

			setValues((prev: FormikValues) => ({
				...prev,
				[EH_PROVIDER]: cmProvider,
				[EH_SOURCE_CONTROL_INTEGRATION_ID]:
					cmSourceControlIntegrationId,
				[EH_CONNECTION_NAME]: cmSourceControlIntegrationName,
				[EH_REPOSITORY]: cmRepository,
				[EH_REPOSITORY_ID]: cmRepositoryId,
				[EH_ADO_ORGANIZATION]: cmAdoOrganization,
				[EH_ADO_PROJECT_NAME]: cmAdoProjectName,
				[EH_REPO_BRANCH]: cmRepositoryBranch,
			}));
		}
	}, [isByoc, values, setValues]);
	const renderHeader = useCallback(
		() => (
			<>
				<HeadingLg text={ehPageTitleOptional} />
				<Text text={ehSubTitle} />
				<HStack>
					<Switch
						aria-labelledby={editingHostSwitchLabelId}
						isChecked={
							isByoc
								? values[CREATE_EDITING_HOST_FOR_BYOC]
								: values[CREATE_EDITING_HOST_FOR_STARTER_KIT]
						}
						onChange={(e: ChangeEvent<HTMLInputElement>) =>
							setFieldValue(
								isByoc
									? CREATE_EDITING_HOST_FOR_BYOC
									: CREATE_EDITING_HOST_FOR_STARTER_KIT,
								e.target.checked,
							)
						}
						data-testid="create-editing-host-switch"
					/>
					<Text
						id={editingHostSwitchLabelId}
						color="black"
						text={ehPageTitle}
					/>
				</HStack>
			</>
		),
		[isByoc, values, setFieldValue, editingHostSwitchLabelId],
	);

	const renderFooter = useCallback(
		() => (
			<FooterBtnGroups
				{...{
					onNextClick: handleNext,
					onPreviousClick: prev,
					isLoading: validationIsLoading || isLoading,
					py: 0,
				}}
			/>
		),
		[handleNext, prev, validationIsLoading, isLoading],
	);

	const editingHostEnabled = useMemo(
		() => isEditingHostEnabled(isByoc, values),
		[isByoc, values],
	);

	return (
		<Stack gap={editingHostEnabled ? '7' : '6'} pb="10">
			{renderHeader()}

			{editingHostEnabled && (
				<>
					<EnvironmentName
						{...{
							values,
							errors,
							touched,
							handleChange,
							setFieldTouched,
							helperText: isByoc ? info4 : '',
							label: nameEhEnv,
							environmentNameSpace: EH_ENVIRONMENT_NAME,
							isDisabled: !isByoc,
						}}
					/>
					<Divider />
					{isByoc ? (
						<>
							<SetupProviderAccount
								{...{
									values,
									errors,
									touched,
									setValues,
									setTouched,
									setErrors,
									setFieldTouched,
									isIntegrationIdValidationValid,
									integrationValidationErrorMsg,
									isSourceControlIntegrationLoading,
									nameSpace: EH,
									isCmEnv: false,
								}}
							/>
							<CurrentRepository
								{...{
									isSourceControlIntegrationLoading:
										isLoading,
									nameSpace: EH,
									accessToken,
									values,
									errors,
									touched,
									setValues,
									setFieldTouched,
									setFieldValue,
									showRepoCombobox: true,
									isByoc,
									handleChange,
								}}
							/>
						</>
					) : (
						<RepositoryDetails />
					)}
					<Divider />
					<Options
						{...{
							nameSpace: EH,
							values,
							errors,
							setFieldValue,
							showProductionCheckbox: false,
						}}
					/>
					<Divider />
					<EnvVars
						{...{
							isExistingProject: false,
							initialVariables: values.ehEnvironmentVariables,
							onVariablesChange: (variables: any) => {
								setFieldValue(
									EH_ENVIRONMENT_VARIABLES,
									variables,
								);
							},
						}}
					>
						<EnvironmentVariables
							variables={values.ehEnvironmentVariables}
						/>
					</EnvVars>
				</>
			)}

			<Divider />
			{renderFooter()}
		</Stack>
	);
};
